import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/category/movie', label: '电影' },
  { to: '/category/tv', label: '电视剧' },
  { to: '/category/variety', label: '综艺' },
  { to: '/news', label: '资讯' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const solid = !isHome || scrolled || menuOpen;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'border-b border-background-200/70 bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" className="group flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-fill text-lg"></i>
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
            黄色<span className="text-primary-500">影视</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-primary-500'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden md:flex" role="search">
            <div className="flex h-10 w-52 items-center gap-2 rounded-full border border-background-200/80 bg-background-100/70 pl-3.5 pr-1 lg:w-60">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视…"
                aria-label="搜索影视内容"
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
              <button
                type="submit"
                aria-label="开始搜索"
                className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="hs-fade-in border-t border-background-200/70 bg-background-50/98 px-4 pb-6 pt-4 backdrop-blur-md lg:hidden">
          <form onSubmit={handleSearch} className="mb-4 flex h-11 items-center gap-2 rounded-full border border-background-200/80 bg-background-100 pl-4 pr-1.5 md:hidden" role="search">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视或资讯…"
              aria-label="搜索影视内容"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
            <button type="submit" aria-label="开始搜索" className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-search-line text-sm"></i>
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `cursor-pointer rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-500/15 text-primary-500' : 'bg-background-100 text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}